import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { REVALIDATE_MODES } from '@savgroup-front-common/constants';
import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { countryList } from '@savgroup-front-common/core/domains/i18n/selectors';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { BaseAutocompleteOptionForHookForm } from '@savgroup-front-common/types';
import { CompanySuggestion } from '@savgroup-front-common/types/CompanyDirectory';

import { PickedCompany } from '../../../OnboardingActivities/OnboardingActivities.types';
import { UPSERT_SALES_LEAD_RESULT } from '../OnboardingForm.constants';
import { findCountryOption } from '../OnboardingForm.helpers';
import OnboardingFormSchema from '../OnboardingForm.schema';

import messages from './messages';
import { useAutoCName } from './useAutoCName';
import useUpsertSalesLeadCommand from './useUpsertSalesLeadCommand';

export interface OnboardingFormFieldValues {
  email: string;
  lastName: string;
  firstName: string;
  companyName: string;
  cName: string;
  phone: string;
  phoneCountryCode: string;
  cgv: boolean;
  address: string;
  postalCode: string;
  city: string;
  countryCode: BaseAutocompleteOptionForHookForm;
  website: string;
}

export interface HandleSelectAddressValues {
  countryCode?: string;
  streetNumber?: string;
  street?: string;
  city?: string;
  postalCode?: string;
}

const useOnboardingFormContext = ({
  handleUserEmail,
}: {
  handleUserEmail: (userEmail: string) => void;
}) => {
  const countryOptions = useSelector(countryList);

  const formContext = useForm<OnboardingFormFieldValues>({
    resolver: yupResolver(OnboardingFormSchema()),
    mode: REVALIDATE_MODES.ON_CHANGE,
    reValidateMode: REVALIDATE_MODES.ON_CHANGE,
  });

  const [pickedCompany, setPickedCompany] = useState<PickedCompany>();
  const [companyWithRegisterAddress, setCompanyWithRegisterAddress] =
    useState<CompanySuggestion>();

  const { handleSubmitSalesLead, isLoading } = useUpsertSalesLeadCommand();
  const autoCName = useAutoCName({ formContext });

  const { setValue, setError } = formContext;

  const handleSelectAddress = useCallback(
    ({ countryCode, city, postalCode }: HandleSelectAddressValues = {}) => {
      const matchingCountry = findCountryOption({
        countryOptions,
        countryCode,
      });

      if (matchingCountry) {
        setValue('countryCode', matchingCountry, { shouldValidate: true });
      }
      setValue('city', city || '');
      setValue('postalCode', postalCode || '');
    },
    [countryOptions, setValue],
  );

  const handleSelectCompany = useCallback(
    (suggestion: CompanySuggestion) => {
      setValue('companyName', suggestion.name, { shouldValidate: true });
      setPickedCompany({
        source: suggestion.source,
        registrationNumber: suggestion.registrationNumber,
      });

      if (!suggestion.address) {
        setCompanyWithRegisterAddress(undefined);

        return;
      }

      setCompanyWithRegisterAddress(suggestion);
      setValue('address', suggestion.address.address || '');
      handleSelectAddress(suggestion.address);
    },
    [handleSelectAddress, setValue],
  );

  const clearPickedCompany = useCallback(() => {
    setPickedCompany(undefined);
    setCompanyWithRegisterAddress(undefined);
  }, []);

  const { resolveCName, isCNameEdited } = autoCName;

  const handleFormSubmit = useCallback(
    async (data: OnboardingFormFieldValues) => {
      const resolvedCName = await resolveCName(data.companyName);

      if (!resolvedCName) {
        if (!isCNameEdited) {
          toast.error(messages.customerSpaceAddressError);
        }

        return;
      }

      const result = await handleSubmitSalesLead({
        ...data,
        cName: resolvedCName.cName,
        pickedCompany,
      });

      if (result === UPSERT_SALES_LEAD_RESULT.INVALID_WEBSITE) {
        setError('website', {
          message: safeFormattedIntlString(messages.invalidWebsite),
        });

        return;
      }

      if (result !== UPSERT_SALES_LEAD_RESULT.SUCCESS) {
        return;
      }

      handleUserEmail(data.email);
    },
    [
      handleSubmitSalesLead,
      handleUserEmail,
      isCNameEdited,
      pickedCompany,
      resolveCName,
      setError,
    ],
  );

  return {
    formContext,
    countryOptions,
    handleFormSubmit,
    handleSelectAddress,
    handleSelectCompany,
    companyWithRegisterAddress,
    pickedCompany,
    clearPickedCompany,
    autoCName,
    isLoading,
  };
};

export default useOnboardingFormContext;
